import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import isEmpty from "lodash/isEmpty";
import debounce from "lodash/debounce";

// constants
import { ACCESS_TYPE } from "@/store/actions/universityDetailsActions";

// components
import NoStudentBox from "./noStudentBox";
import Button from "@/components/shared/Button";
import StudentTab from "./studentList";
import UniversityTab from "./university";
import LiveReport from "./liveReport";

// actions
import { setShowStudentDetailsInMobile } from "@/store/actions/commonActions";
import { reportsGetAndStore } from "@/store/actions/reportsActions";

// mui
import { Box, Grid, makeStyles, Tab, Tabs, useMediaQuery, useTheme, withStyles } from "@material-ui/core";
import ArrowBackIosRoundedIcon from "@material-ui/icons/ArrowBackIosRounded";
import FiberManualRecordIcon from "@material-ui/icons/FiberManualRecord";

const useStyles = makeStyles((theme) => ({
    detailsTabs: {
        backgroundColor: theme.palette.common.white,
        marginBottom: theme.spacing(4),
    },
    studentDetailsContainer: {
        margin: theme.spacing(0, 4),
        [theme.breakpoints.down("sm")]: {
            margin: theme.spacing(9, 0, 0),
        },
    },
    studentDetailsItem: {
        padding: theme.spacing(2),
        [theme.breakpoints.down("md")]: {
            padding: theme.spacing(1),
        },
    },
    backArrow: {
        fontSize: theme.spacing(2),
    },
    tabsContainer: { height: "fit-content" },
    labelWithDot: {
        display: "flex",
        alignItems: "center",
        gap: "4px",
    },
}));
const DetailsTabs = withStyles((theme) => ({
    root: {
        borderBottom: "none",
        [theme.breakpoints.down("sm")]: {
            borderBottom: "1px solid #e8e8e8",
            marginBottom: theme.spacing(1),
        },
    },
}))(Tabs);

const DetailTab = withStyles((theme) => ({
    root: {
        textTransform: "none",
        minWidth: 100,
        [theme.breakpoints.down("sm")]: {
            fontSize: theme.spacing(1.5),
        },
        "&:hover": {
            color: theme.palette.primary.main,
            opacity: 1,
        },
        "&$selected": {
            color: theme.palette.primary.main,
            fontWeight: "600",
        },
        "&:focus": {
            color: theme.palette.primary.main,
        },
    },
    selected: {},
}))((props) => <Tab disableRipple {...props} />);

export default function DataTabs() {
    const classes = useStyles();
    const dispatch = useDispatch();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const location = useSelector((state) => state.router.location);
    const universityDetails = useSelector((state) => state.universityDetails.data);
    const studentDetailCount = useSelector((state) => state.studentDetails.count);
    const totalStudents = useSelector((state) => state.reports.totalStudents);

    const [selectedTab, setSelectedTab] = useState(0);

    const search = new URLSearchParams(location.search);
    const noRegistrationCode = isEmpty(search.get("registrant_code"));

    const handleTabChange = (_, value) => {
        setSelectedTab(value);
    };

    const fetchReports = React.useCallback(
        debounce((universityDetails, page, perPage) => {
            if (isEmpty(universityDetails)) return;

            dispatch(
                reportsGetAndStore({
                    access: universityDetails.showPU ? ACCESS_TYPE.counsellor : ACCESS_TYPE.university,
                    university_code: universityDetails.universityCode,
                    university_name: universityDetails.universityName,
                    fair_id: universityDetails.fairID,
                    page,
                    per_page: perPage,
                })
            );
        }, 300),
        []
    );

    const tabsForOnlineEvent = (totalStudents) => {
        const tabs = [
            { label: "Participating Universities", Component: UniversityTab },
            { label: "Student Details", Component: StudentTab },
            {
                label: (
                    <span className={classes.labelWithDot}>
                        <FiberManualRecordIcon fontSize="inherit" color="inherit" />
                        <span>{totalStudents ? `Live Report (${totalStudents})` : "Live Report"}</span>
                    </span>
                ),
                Component: LiveReport,
            },
        ];
        return [tabs, 2];
    }

    const tabsForOfflineEvent = (totalStudents, showPU) => {
        const tabs = [];
        let liveReportTabNumber = 1;
        if (showPU) {
            tabs.push({ label: "Participating Universities", Component: UniversityTab });
            liveReportTabNumber = 2;
        }
        tabs.push({ label: "Student Details", Component: StudentTab });
        tabs.push({
            label: (
                <span className={classes.labelWithDot}>
                    <FiberManualRecordIcon fontSize="inherit" color="inherit" />
                    <span>{totalStudents ? `Live Report (${totalStudents})` : "Live Report"}</span>
                </span>
            ),
            Component: LiveReport,
        });

        return [tabs, liveReportTabNumber];
    }

    const [tabsData, liveReportTabNumber] = React.useMemo(() => {
        const fn = universityDetails.isOnlineEvent ? tabsForOnlineEvent : tabsForOfflineEvent;
        return fn(totalStudents, universityDetails.showPU)
    }, [universityDetails.isOnlineEvent, universityDetails.showPU, totalStudents]);

    React.useEffect(() => {
        if (selectedTab === liveReportTabNumber) {
            fetchReports(universityDetails, 1, 10);
        }
    }, [selectedTab, universityDetails]);

    React.useEffect(() => {
        if (selectedTab === liveReportTabNumber && !noRegistrationCode) {
            setSelectedTab(0);
        }
    }, [studentDetailCount]);

    if (noRegistrationCode) {
        return (
            <Grid container>
                <Grid container item sm={12}>
                    <Box width="100%" mx={2} mb={2}>
                        <NoStudentBox />
                    </Box>
                </Grid>
            </Grid>
        );
    }

    return (
        <Grid container>
            {isMobile && (
                <Box mx={1}>
                    <Button
                        onClick={() => {
                            dispatch(setShowStudentDetailsInMobile(false));
                        }}
                        variant="text"
                        label="Back to home"
                        startIcon={<ArrowBackIosRoundedIcon />}
                    />
                </Box>
            )}
            <Grid container item sm={12}>
                <Box
                    width="100%"
                    mx={2}
                    mb={2}
                    style={
                        selectedTab === liveReportTabNumber
                            ? { overflowX: "hidden", overflowY: "visible" }
                            : { overflow: "hidden" }
                    }
                >
                    <DetailsTabs
                        value={selectedTab}
                        className={classes.detailsTabs}
                        onChange={handleTabChange}
                        indicatorColor="primary"
                    >
                        {tabsData.map((tab) => (
                            <DetailTab key={tab.label} label={tab.label} />
                        ))}
                    </DetailsTabs>
                    {tabsData.map((tab, index) => (
                        <React.Fragment key={tab.label}>
                            {selectedTab == index && <tab.Component />}
                        </React.Fragment>
                    ))}
                </Box>
            </Grid>
        </Grid>
    );
}
